import type React from "react";
import {decompressFromEncodedURIComponent} from "lz-string";

export type GptColorPair = {
   bg?: string;
   text?: string;
};

export type GptWidgetButtonColors = {
   bg?: string;
   icon?: string;
   border?: string;
   shadow?: string;
   hover?: string;
};

export type GptColors = {
   chatBubble?: GptColorPair;
   background?: GptColorPair;
   assistantText?: string;
   primary?: GptColorPair;
   input?: GptColorPair;
   widgetButton?: GptWidgetButtonColors;
};

// Hex: #rgb, #rgba, #rrggbb, #rrggbbaa
const HEX_COLOR = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
// CSS named colors and keywords ("transparent", "currentcolor", "inherit", etc.)
const NAMED_COLOR = /^[a-z]+$/i;
// CSS color functions; the body is restricted to numbers, percentages, commas,
// whitespace, slashes, plus/minus, periods, and letters (for "none", "deg", etc.).
// This intentionally blocks things like `url(...)`, `image-set(...)`, quotes,
// semicolons, and nested parentheses.
const COLOR_FN =
   /^(?:rgb|rgba|hsl|hsla|hwb|lab|lch|oklab|oklch|color)\(\s*[0-9a-z%.,\-/+\s]+\s*\)$/i;

/**
 * Defense-in-depth validator for embedder-supplied color strings. Accepts hex,
 * a restricted subset of CSS color functions, and bare identifiers (named
 * colors). Rejects anything that could smuggle other CSS (url/image-set/etc.).
 */
export const isValidCssColor = (value: string): boolean => {
   const trimmed = value.trim();
   if (trimmed.length === 0 || trimmed.length > 64) return false;
   return HEX_COLOR.test(trimmed) || NAMED_COLOR.test(trimmed) || COLOR_FN.test(trimmed);
};

const pickString = (value: unknown): string | undefined => {
   if (typeof value !== "string" || value.length === 0) return undefined;
   return isValidCssColor(value) ? value : undefined;
};

const pickColorPair = (value: unknown): GptColorPair | undefined => {
   if (!value || typeof value !== "object") return undefined;
   const source = value as Record<string, unknown>;
   const bg = pickString(source.bg);
   const text = pickString(source.text);
   if (!bg && !text) return undefined;
   const pair: GptColorPair = {};
   if (bg) pair.bg = bg;
   if (text) pair.text = text;
   return pair;
};

// Conservative upper bounds to guard against decompression-bomb style payloads.
// A fully populated legitimate payload is well under 500 chars both before and
// after decompression, so 4 KiB is already very generous.
const MAX_ENCODED_LENGTH = 4096;
const MAX_DECOMPRESSED_LENGTH = 4096;

/**
 * Decode an LZ-String encoded `colors` query param into a validated `GptColors`
 * object. Returns `null` on any failure (invalid compression, invalid JSON,
 * missing fields, or oversized input) so callers can fall back to existing
 * brand colors safely.
 */
export const decodeGptColors = (raw: string | null): GptColors | null => {
   if (!raw) return null;
   if (raw.length > MAX_ENCODED_LENGTH) return null;

   let parsed: unknown;
   try {
      const decompressed = decompressFromEncodedURIComponent(raw);
      if (!decompressed) return null;
      if (decompressed.length > MAX_DECOMPRESSED_LENGTH) return null;
      parsed = JSON.parse(decompressed);
   } catch {
      return null;
   }

   if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;

   const source = parsed as Record<string, unknown>;
   const result: GptColors = {};

   const chatBubble = pickColorPair(source.chatBubble);
   if (chatBubble) result.chatBubble = chatBubble;

   const background = pickColorPair(source.background);
   if (background) result.background = background;

   const assistantText = pickString(source.assistantText);
   if (assistantText) result.assistantText = assistantText;

   const primary = pickColorPair(source.primary);
   if (primary) result.primary = primary;

   const input = pickColorPair(source.input);
   if (input) result.input = input;

   return Object.keys(result).length > 0 ? result : null;
};

/**
 * Style applied to the outermost iframe wrapper so the entire chat surface
 * reflects the embedder's `background` choice.
 */
export const getIframeBackgroundStyle = (
   colors: GptColors | null | undefined
): {backgroundColor?: string; color?: string} => {
   const bg = colors?.background?.bg;
   const text = colors?.background?.text;
   const style: {backgroundColor?: string; color?: string} = {};
   if (bg) style.backgroundColor = bg;
   if (text) style.color = text;
   return style;
};

/**
 * Style for the user chat bubble. Falls back to the supplied brand-derived
 * values when no override is provided.
 */
export const getUserBubbleStyle = (
   colors: GptColors | null | undefined,
   fallbackBg: string,
   fallbackText: string
): {backgroundColor: string; color: string} => ({
   backgroundColor: colors?.chatBubble?.bg ?? fallbackBg,
   color: colors?.chatBubble?.text ?? fallbackText,
});

/**
 * Inline style for the assistant message content container. Only sets `color`
 * when the embedder has opted in, letting the default `text-foreground`
 * Tailwind class take over otherwise.
 */
export const getAssistantContentStyle = (
   colors: GptColors | null | undefined
): {color?: string} => {
   const color = colors?.assistantText;
   return color ? {color} : {};
};

const colorPairToStyle = (
   pair: GptColorPair | undefined
): {backgroundColor?: string; color?: string} => {
   const style: {backgroundColor?: string; color?: string} = {};
   if (pair?.bg) style.backgroundColor = pair.bg;
   if (pair?.text) style.color = pair.text;
   return style;
};

/**
 * Inline style for the primary send (ask question) button. Returns an empty
 * object when no override is set so the brand-derived Tailwind classes win.
 */
export const getPrimaryButtonStyle = (
   colors: GptColors | null | undefined
): {backgroundColor?: string; color?: string} => colorPairToStyle(colors?.primary);

/**
 * Inline style for the chat input textarea. Returns bg/text overrides and, when
 * a `primary.bg` override is present, also sets the Tailwind ring CSS variable
 * so the focus ring adopts the primary color instead of the default blue. When
 * `input.text` is set, also exposes it as `--chat-placeholder-color` so the
 * `::placeholder` rule in `chat.css` can reuse the color at reduced opacity.
 */
export const getInputStyle = (
   colors: GptColors | null | undefined
): React.CSSProperties => {
   const base = colorPairToStyle(colors?.input);
   const ring = colors?.primary?.bg;
   const placeholderColor = colors?.input?.text;

   const style: Record<string, string | undefined> = {...base};
   if (ring) {
      // --tw-ring-color drives Tailwind's `ring-*` utilities; inline wins over
      // non-!important classes like `focus:ring-gailPrimary`.
      style["--tw-ring-color"] = ring;
   }
   if (placeholderColor) {
      style["--chat-placeholder-color"] = placeholderColor;
   }
   return style as React.CSSProperties;
};
